import { AccountInfo } from "@azure/msal-browser";
import axios from "./HttpClient";
import { GetUserInfo } from "./IdentityAPI";

const serviceManagerURL = ServiceManagerUrl;

export function IsRenderable(mimeType) {
    const renderableMimeTypes = ["application/pdf", "image/jpeg", "image/png", "image/gif", "text/html", "audio/mpeg", "video/mp4"];
    return renderableMimeTypes.includes(mimeType);
}

export function GetCampaign(campaignId) {
    return axios.get(`${serviceManagerURL}api/Campaigns/${campaignId}`);
}

export function StartQuestionnaire(campaign: Campaign) {
    return GetUserInfo().then((res) => {
        return axios
            .post(`${serviceManagerURL}api/questionnaires`, {
                campaignID: campaign.id,
                organizationID: campaign.organizationID,
                questionnaireTypeID: campaign.questionnaireTypeID,
                // @ts-ignore
                subjectID: res.data.sub,
                subjectType: campaign.questionnaireType?.subjectType,
                allowComments: campaign.allowComments,
                allowUploads: campaign.allowUploads,
            })
            .then((response) => {
                return response.data.result;
            });
    });
}

export function StartImpersonateQuestionnaire(campaign: Campaign, subject) {
    return axios
        .post(`${serviceManagerURL}api/questionnaires`, {
            campaignID: campaign.id,
            organizationID: campaign.organizationID,
            questionnaireTypeID: campaign.questionnaireTypeID,
            // @ts-ignore
            subjectID: subject,
            subjectType: campaign.questionnaireType?.subjectType,
            allowComments: campaign.allowComments,
            allowUploads: campaign.allowUploads,
        })
        .then((response) => {
            return response.data.result;
        });
}

export function QuestionnaireActions() {
    return axios.get(`${serviceManagerURL}api/questionnaires/myactions`).then((response) => {
        return response.data.result;
    });
}

export function GetQuestionnaire(id) {
    return axios.get(`${serviceManagerURL}api/questionnaires/${id}`).then((response) => {
        return response.data.result;
    });
}

export function GetMyQuestionnaires() {
    return axios.get(`${serviceManagerURL}api/questionnaires/MyQuestionnaires`).then((response) => {
        return response.data.result;
    });
}

export function UploadAttachment(attachment) {
    return axios.post(`${serviceManagerURL}api/attachments`, attachment).then((response) => {
        return response.data.result;
    });
}

export function DeleteAttachment(attachmentId) {
    return axios.delete(`${serviceManagerURL}api/attachments/${attachmentId}`).then((response) => {
        return response.data.result;
    });
}

export function AnswerQuestion(questionnaire, question, set, option) {
    return axios.post(`${serviceManagerURL}api/answers/option`, option, {
        params: {
            questionnaire,
            question,
            set,
        },
    });
}

export function AddSet(questionnaireId, questionId) {
    return axios.post(`${serviceManagerURL}api/questionanswersets`, { questionId, questionnaireId }).then((response) => {
        return response.data.result;
    });
}

export function RemoveSet(setId) {
    return axios.delete(`${serviceManagerURL}api/questionanswersets/${setId}`).then((response) => {
        return response.data.result;
    });
}

export function SubmitQuestionnaire(id) {
    return axios.post(`${serviceManagerURL}api/questionnaires/$submit`, { questionnaireID: id }).then((response) => {
        return response.data.result;
    });
}

export function GetCampaignSubmissions(id) {
    return axios.get(`${serviceManagerURL}api/campaigns/${id}/$submissions`).then((response) => {
        return response.data.result;
    });
}

export function GetReferenceLists() {
    return axios.get("/api/ReferenceData/list");
}

export function GetReferenceList(id) {
    return axios.get("/api/ReferenceData/list/" + id);
}

export interface GetUserQuestionnaireResponse {
    questionnaires: Questionnaire[];
    campaigns: Campaign[];
}

export class CampaignStatistics {
    submitted: number;
    questionsAnswered: number;
    required: number;
    requiredAnswered: number;
    maxScore: number;
    score: number;
    totalQuestionnaires: number;
}

export class Campaign {
    id: string;
    name: string;
    organizationID?: string;
    key: string;
    // organization?: Organization;
    questionnaireTypeID: string;
    questionnaireType?: QuestionnaireType;
    delegated: boolean;
    allowComments: boolean;
    allowUploads: boolean;
    accessType: number;
    description: string;
    data: CampaignData;
    questionnaires?: Array<Questionnaire>;
    startDate: string | null;
    endDate: string | null;
    flagDate: string | null;
    statistics?: CampaignStatistics;
    campaignFrequency: number;
    accessPolicies: any[];

    constructor(organizationId?: string) {
        this.organizationID = organizationId;
        this.data = new CampaignData();
        this.accessType = 3;
        this.name = "";
        this.description = "";
        this.key = "";
        this.delegated = false;
        this.questionnaires = [];
        this.accessPolicies = [];
    }
}

export class CampaignData {
    constructor() {
        this.icon = "comments-question-check";
        this.groups = [];
        this.allowStaffAsSubjects = false;
        this.allowStudentsAsSubjects = false;
        this.allowParentsAsSubjects = false;
        this.allowGuestsAsSubjects = false;
        this.questionnaireReviews = [];
    }

    groups: string[];
    questionnaireReviews: QuestionnaireReviewConfig[];
    allowStaffAsSubjects?: boolean;
    allowStudentsAsSubjects?: boolean;
    allowParentsAsSubjects?: boolean;
    allowGuestsAsSubjects?: boolean;
    icon: string;
}

export interface QuestionnaireReviewConfig {
    name: string;
    reviewGroupType: string;
    visibleToSubject: boolean;
    visibleToAll: boolean;
}

export interface IQuestionnaireInstance {
    questionnaire: Questionnaire;
    groups: Group[];
}

export class Questionnaire {
    id: string;
    timestamp: string;
    name: string;
    description: string;
    questionnaireTypeID: string;
    questionnaireType: QuestionnaireType;
    campaignID: string;
    campaign: Campaign;
    organizationID: string;
    // organization: Organization;
    subjectID: string;
    subjectType: number;
    allowComments: boolean;
    allowUploads: boolean;
    locked: boolean;
    lastSubmissionDate: string;
    answers: Array<Answer>;
    comments: Array<Comment>;
    accessPolicies: Array<any>;
    questions: Array<Question>;
    score: number;
    maxScore: number;
    totalQuestions: number;
    answeredQuestions: number;
    requiredQuestions: number;
    requiredAnsweredQuestions: number;
    flagged: boolean;

    questionnaireReviews: QuestionnaireReview[];
    reviewedBy: QuestionnaireReview[];
    reviews: QuestionnaireReview[];
}

export interface GetUserQuestionnaireResponse {
    questionnaires: Questionnaire[];
    campaigns: Campaign[];
}

export interface QuestionnaireReportGroup {
    groupID: string;
    groupName: string;
    sortKey: number;
    questions: QuestionnaireReportQuestion[];
}

export interface QuestionnaireReportQuestion {
    questionID: string;
    questionText: string;
    sortKey: number;
    allowSets: boolean;
    answers: QuestionnaireReportAnswer[];
}

export interface QuestionnaireReportAnswer {
    answerID: string;
    answerValue: string;
    questionnaireID: string;
    setID: string;
    optionID: string;
    optionText: string;
    optionValue: string;
    optionType: number;
    optionSortKey: number;
    optionScore: number;
}

export interface QuestionnaireReview {
    id: string;
    name: string;
    questionnaireID: string;
    questionnaire: Questionnaire;
    reviewedByQuestionnaireID: string;
    reviewedByQuestionnaire: Questionnaire;
    reviewer: string;
    visibleToSubject: boolean;
    visibleToAll: boolean;
}

export interface CreateQuestionnaireReviewRequest {
    questionnaireID: string;
    subjectID: string;
    reviewName: string;
    visibleToAll: boolean;
    visibleToSubject: boolean;
}

export class Question {
    id: string;
    text: string;
    subtext: string;
    key: string;
    multipleOptions: boolean;
    weight: number;
    sortKey: number;
    allowSets: boolean;
    allowComments: boolean;
    allowUploads: boolean;
    required: boolean;
    conditions: Condition[];
    groups: GroupQueryModel[];
    options: Option[];
    organizationID: string;
    tags: TagAPIModel[];
}

export class QuestionTag {
    id: string;
    questionID: string;
    tagID: string;
    tag: TagAPIModel;
}

export class Condition {
    id: string;
    questionID: string;
    question: Question;
    reliesOnID: string;
    reliesOn: Question;
    type: number;
    conditionOptions: Array<ConditionOption>;
}

export class ConditionOption {
    id: string;
    optionID: string;
    option?: Option;
}

export interface QuestionGroup {
    id: string;
    questionID: string;
    question: Question;
    groupID: string;
    group: Group;
}

export interface QuestionQueryModel {
    id: string;
    text: string;
    subtext: string;
    key: string;
    multipleOptions: boolean;
    weight: number;
    sortKey: number;
    allowSets: boolean;
    allowComments: boolean;
    allowUploads: boolean;
    required: boolean;
    conditions: Condition[];
    groups: GroupQueryModel[];
    options: Option[];
    organizationID: string;
    tags: TagAPIModel[];
}

export interface GroupQueryModel {
    id: string;
    name: string;
    weight: number;
    sortKey: number;
}

export class Option {
    id: string;
    questionID?: string;
    text: string;
    score: number;
    sortKey: number;
    type: any;
    value?: any;
    key?: string;
    referenceListID?: string;
}

export interface TagAPIModel {
    id: string;
    name: string;
    color: string;
}
export class Group {
    id: string;
    name: string;
    weight: number;
    sortKey: number;
    questions?: Array<Question>;
    questionnaireTypes?: Array<QuestionnaireType>;
    selected: boolean;
    organizationID?: string;
}

export class Answer {
    id: string;
    value: string;
    optionID: string;
    option: Option;
    questionID: string;
    question: Question;
    setID: string;
    set: any;
    questionnaireID: string;
    questionnaire: Questionnaire;
}

export interface QuestionnaireTypeAccessPolicy {
    accessType: number;
    questionnaireTypeID: string;
    rule: string;
    questionnaireAccessType: number;
}

export class QuestionnaireType {
    id: string;
    name: string;
    groups: Array<Group>;
    selected: boolean;
    subjectType: number;
    accessPolicies: Array<QuestionnaireTypeAccessPolicy>;
    organizationID: string;
    tags: Array<TagAPIModel>;
}

export interface ApplicationAction {
    text: string;
    uRL: string;
    icon: string;
    type: ApplicationActionType;
    data: any;
}

export enum ApplicationActionType {
    ExistingQuestionnaire = 1,
    NewQuestionnaire = 2,
}
